import React, { useState } from "react";
import { ConsentControls } from "./ConsentControls";
import { COOKIE_POLICY_PATH } from "../../../shared/constants";
import classNames from "classnames";
import styles from "../styles/styles.module.scss";
import { Consent } from "banco-website/Cookies/shared/Cookiebot";
import { cookiesRequiredForCategory } from "../../../shared/helpers";

import { Button } from "banco-components";

export const BANNER_HEADINGS = {
  DEFAULT: "We use essential, analytic and marketing cookies to enhance your experience,"
    + " improve our site, strengthen security, and to help us advertise on other sites.",
  NO_MARKETING: "We use essential and analytic cookies to enhance your experience, improve"
    + " our site, and strengthen security."
};

/* eslint-disable max-statements */
export const CookieConsentBanner = ({ cookiebot = window.Cookiebot }) => {
  // State
  const defaultSelection = {
    preferences: false, // not selectable / not required
    statistics: false,
    marketing: false,
  };
  const initialBannerState = !cookiebot.hasResponse;
  const [bannerVisible, setBannerVisible] = useState<boolean>(initialBannerState);
  const [showCookieSettings, setShowCookieSettings] = useState<boolean>(false);
  const [userSelection, setUserSelection] = useState<Consent>(defaultSelection);

  const MARKETING_COOKIES_REQUIRED = cookiesRequiredForCategory("marketing");
  const RENDERED_BANNER_HEADING =
  MARKETING_COOKIES_REQUIRED ? BANNER_HEADINGS.DEFAULT : BANNER_HEADINGS.NO_MARKETING;

  // Submission
  const handleSubmit = (acceptAll: boolean = false) => {
    const marketingState = () => {
      if (acceptAll) {
        return MARKETING_COOKIES_REQUIRED ? true : false;
      } else {
        return userSelection.marketing;
      }
    };

    cookiebot.submitCustomConsent(
      userSelection.preferences, // use default, non selectable
      acceptAll ? true : userSelection.statistics,
      marketingState()
    );
    setBannerVisible(false);
  };

  // Animations
  const defaultControlsTransitionStyle = {
    transition: "max-height 300ms cubic-bezier(.17,.67,.83,.67)",
    maxHeight: 68,
    overflow: "hidden"
  };
  // The final desired height is required to force CSS max-height transitions.
  const controlsHeight = 244;
  const controlsTransitionStyles = {
    entered: { maxHeight: controlsHeight, marginBottom: "1rem" },
    exited: { maxHeight: 0, overflow: "hidden" }
  };

  const renderHeader = () => {
    if (showCookieSettings) {
      return null;
    }
    return (
      <header className={ styles.header } data-fb-panel="Header Element">
        <p className={ styles.header_text }>
          { RENDERED_BANNER_HEADING }
        </p>
        <p
          className={ styles.header_text }>
          Click here to { " " }
          <span
            data-fb-link="Manage Cookie Settings Link"
            className={ styles.a }
            onClick={ () => setShowCookieSettings(true) }
            onKeyPress={ (e) => e.key === "Enter" ? setShowCookieSettings(true) : null }
            role="button"
            tabIndex={ 0 }
          >
            manage cookie settings.
          </span>
        </p>
      </header>
    );
  };

  const renderControls = () => {
    return (
      <div
        data-fb-panel="Cookie Settings Panel"
        style={ {
          ...defaultControlsTransitionStyle,
          ...controlsTransitionStyles[showCookieSettings ? "entered" : "exited"]
        } }
      >
        <ConsentControls
          userSelection={ userSelection }
          setUserSelection={ setUserSelection }
        />
      </div>
    );
  };

  const renderActionLinks = () => {
    if (showCookieSettings) {
      return (
        <>
          <a
            href={ COOKIE_POLICY_PATH }
            className={ `btn ${classNames([styles.consentButton, styles.consentOutlineButton])}` }
            data-fb-link={ "Cookie Notice Link" }
            target="_blank"
          >
            Cookie Notice
          </a>
          <Button
            className={ classNames([styles.consentButton, styles.consentPrimaryButton]) }
            onClick={ handleSubmit.bind(null, false) }
            data-fb-button={ "Accept User Selected Cookies" }
          >
            Accept cookie settings
          </Button>
        </>
      );
    }

    return (
      <>
        <Button
          className={ classNames([styles.consentButton, styles.consentOutlineButton]) }
          variant="outline-primary"
          onClick={ handleSubmit.bind(null, false) }
          disabled={ !bannerVisible }
          data-fb-button={ "Decline Non-Essential Cookie Consent" }
        >
          Decline non-essential
        </Button>
        <Button
          className={ classNames([styles.consentButton, styles.consentPrimaryButton]) }
          onClick={ handleSubmit.bind(null, true) }
          disabled={ !bannerVisible }
          data-fb-button={ "Accept All Cookies" } // used by acceptance specs to hide banner
        >
          Accept all
        </Button>
      </>
    );
  };

  return (
    <>
      <div
        className={
          classNames({
            [styles.bannerContainer]: true,
            [styles.bannerClosed]: !bannerVisible
          })
        }
      >
        <div className={ styles.contentContainer }>
          { renderHeader() }
          { renderControls() }
          <div className={ styles.actionButtonContainer }>
            { renderActionLinks() }
          </div>
        </div>
      </div>
    </>
  );
};
